import { ApiError } from './client';
import { ADAPTIVE_PROTOCOL, PARALLEL_PROTOCOL, uploadParallelRanges, type UploadLane } from './parallelFileUpload';
import { browserUploadConnectionLimit } from './uploadConcurrency';

export interface FileBrowserUploadTarget { assetId: number; pathName: string; relativePath: string }
export interface ExplorerUploadTarget { rootId: string; path: string; context?: { assetId: number; pathName: string } }
export interface RemoteFileSource {
  name: string; size: number;
  validate: (signal?: AbortSignal) => Promise<void>;
  readChunk: (start: number, end: number, signal?: AbortSignal) => Promise<Blob>;
}
export type BrowserUploadSource = File | RemoteFileSource;
export interface UploadState { uploadId: string; received: number; chunkSize?: number; status: 'uploading' | 'publishing' | 'complete'; filename?: string; size?: number; protocol?: string; parallelism?: number; concurrencyLimit?: number; lanes?: UploadLane[] }
const ENDPOINT = '/api/attachments/file-upload';
const LEGACY_CHUNK = 1024 * 1024;
const MAX_CHUNK = 4 * LEGACY_CHUNK;

export function newFileUploadId() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
}

async function step<T = UploadState>(path: string, method: string, body: unknown, signal?: AbortSignal, extraHeaders: Record<string, string> = {}): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.throwIfAborted();
  signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 5 * 60 * 1000);
  try {
    const binary = body instanceof Blob;
    const response = await fetch(path, {
      method, credentials: 'same-origin', signal: controller.signal,
      headers: { 'Content-Type': binary ? 'application/octet-stream' : 'application/json', 'X-Skrepka-Request': '1', ...extraHeaders },
      body: binary ? body : JSON.stringify(body),
    });
    const payload: unknown = await response.json().catch(() => undefined);
    const detail: unknown = typeof payload === 'object' && payload !== null && 'data' in payload ? payload.data : undefined;
    if (!response.ok) {
      const message = typeof detail === 'string' ? detail : typeof detail === 'object' && detail !== null && 'error' in detail && typeof detail.error === 'string' ? detail.error : '';
      throw new ApiError(message || (response.status === 507 ? 'На диске назначения недостаточно свободного места.' : `Не удалось загрузить файл (HTTP ${response.status}).`), response.status);
    }
    return detail as T;
  } catch (error) {
    signal?.throwIfAborted();
    if (error instanceof ApiError) throw error;
    throw new ApiError('Связь прервалась. Нажмите «Повторить», чтобы продолжить загрузку.', 0);
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

async function retry<T>(operation: () => Promise<T>, status?: (text: string) => void, signal?: AbortSignal): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    signal?.throwIfAborted();
    try { return await operation(); }
    catch (error) {
      signal?.throwIfAborted();
      if (attempt >= 4 || !(error instanceof ApiError) || ![0, 408, 429, 500, 502, 503, 504].includes(error.status)) throw error;
      status?.('Связь прервалась. Повторяем передачу…');
      await new Promise<void>((resolve, reject) => {
        const onAbort = () => { clearTimeout(timer); reject(new DOMException('Загрузка остановлена', 'AbortError')); };
        const timer = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, 1000 * 2 ** attempt);
        signal?.addEventListener('abort', onAbort, { once: true });
        if (signal?.aborted) onAbort();
      });
    }
  }
}

/** Original bytes only; never routes through attachment media conversion. */
export async function uploadFileBrowserFile(file: BrowserUploadSource, target: FileBrowserUploadTarget | ExplorerUploadTarget, uploadId: string,
  onProgress?: (received: number) => void, onStatus?: (text: string) => void, signal?: AbortSignal): Promise<UploadState> {
  if (!Number.isSafeInteger(file.size) || file.size < 0) throw new ApiError('Не удалось определить размер файла.', 400);
  if ('readChunk' in file) await retry(() => file.validate(signal), onStatus, signal);
  const path = `${ENDPOINT}/${encodeURIComponent(uploadId)}`;
  const validate = (state: UploadState, expected?: number) => {
    if (!state || state.uploadId !== uploadId || !Number.isSafeInteger(state.received) || state.received < 0 || state.received > file.size ||
        !['uploading', 'publishing', 'complete'].includes(state.status) || (expected !== undefined && state.received !== expected) ||
        (state.status !== 'uploading' && state.received !== file.size) ||
        (state.size !== undefined && state.size !== file.size)) {
      throw new ApiError('Сервер не подтвердил переданный фрагмент файла.', 502);
    }
    return state;
  };
  const protocol = file.size >= 2 * LEGACY_CHUNK && globalThis.crypto?.subtle ? { protocol: ADAPTIVE_PROTOCOL } : {};
  // Remote copy sources retain their bounded 1 MiB reads. Old sessions keep
  // the size already confirmed by the server, even after a browser refresh.
  const maxChunkSize = 'readChunk' in file ? LEGACY_CHUNK : MAX_CHUNK;
  let state = await retry(async () => validate(await step(`${ENDPOINT}/start`, 'POST', { ...target, name: file.name, size: file.size, uploadId, maxChunkSize, ...protocol }, signal)), onStatus, signal);
  onProgress?.(state.received);
  if (state.status === 'complete') return state;
  // A lost finish response can leave an atomic publication in progress.
  // Its bytes are already present: retry finish, never send them again.
  const chunkSize = state.chunkSize ?? LEGACY_CHUNK;
  if (!Number.isSafeInteger(chunkSize) || chunkSize < 1 || chunkSize > maxChunkSize) throw new ApiError('Сервер вернул неверный размер фрагмента.', 502);
  if (state.protocol !== undefined && ![PARALLEL_PROTOCOL, ADAPTIVE_PROTOCOL].includes(state.protocol)) throw new ApiError('Неизвестный режим загрузки. Обновите Скрепку.', 502);
  if (state.protocol === PARALLEL_PROTOCOL || state.protocol === ADAPTIVE_PROTOCOL) {
    state = await uploadParallelRanges({ file, initial: state, signal, onProgress, onStatus,
      connectionLimit: () => browserUploadConnectionLimit(`${ENDPOINT}/start`),
      read: async (start, end, rangeSignal) => 'readChunk' in file
        ? retry(() => file.readChunk(start, end, rangeSignal), onStatus, rangeSignal)
        : file.slice(start, end),
      send: (lane, offset, chunk, checksum, rangeSignal) => retry(async () => validate(await step(
        `${path}?offset=${offset}&lane=${lane}`, 'PUT', chunk, rangeSignal,
        { 'X-Skrepka-Part-SHA256': checksum })), onStatus, rangeSignal),
    });
  }
  while (state.received < file.size) {
    const offset = state.received;
    const end = Math.min(offset + chunkSize, file.size);
    // Only one bounded slice is held at a time, even for multi-terabyte Files.
    const bytes = 'readChunk' in file
      ? await retry(() => file.readChunk(offset, end, signal), onStatus, signal)
      : file.slice(offset, end);
    const chunk = new File([bytes], file.name, { type: 'application/octet-stream' });
    state = await retry(async () => validate(await step(`${path}?offset=${offset}`, 'PUT', chunk, signal), end), onStatus, signal);
    onStatus?.('');
    onProgress?.(state.received);
  }
  onStatus?.('Завершаем сохранение…');
  if ('readChunk' in file) await retry(() => file.validate(signal), onStatus, signal);
  state = await retry(async () => {
    const result = validate(await step(path, 'POST', {}, signal), file.size);
    if (result.status !== 'complete') throw new ApiError('Сервер ещё не подтвердил сохранение файла.', 502);
    return result;
  }, onStatus, signal);
  onStatus?.('');
  return state;
}

/** Confirm start before cancel, including when its first response was lost.
 * A delayed identical start will then see the cancelled session, not create a new part.
 */
export async function cancelFileBrowserUpload(file: BrowserUploadSource, target: FileBrowserUploadTarget | ExplorerUploadTarget, uploadId: string): Promise<UploadState | { uploadId: string; status: 'cancelled' }> {
  const path = `${ENDPOINT}/${encodeURIComponent(uploadId)}`;
  try {
    const current = await retry(() => step(`${ENDPOINT}/start`, 'POST', { ...target, name: file.name, size: file.size, uploadId }));
    if (!current || current.uploadId !== uploadId || current.size !== file.size || !Number.isSafeInteger(current.received) || current.received < 0 || current.received > file.size) {
      throw new ApiError('Сервер не подтвердил состояние загрузки. Отмену можно повторить.', 502);
    }
    if (current.status === 'complete' && current.received === file.size) return current;
    if (current.status === 'publishing' && current.received === file.size) {
      // The file is already being published; finish its transaction without deleting it.
      return await uploadFileBrowserFile(file, target, uploadId);
    }
    if (current.status !== 'uploading') throw new ApiError('Сервер не подтвердил состояние загрузки. Отмену можно повторить.', 502);
    let cancelled: { uploadId: string; status: 'cancelled' };
    try {
      cancelled = await retry(() => step<{ uploadId: string; status: 'cancelled' }>(path, 'DELETE', undefined));
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const latest = await retry(() => step(path, 'GET', undefined));
        if (latest?.uploadId === uploadId && latest.size === file.size && latest.received === file.size) {
          if (latest.status === 'complete') return latest;
          if (latest.status === 'publishing') return await uploadFileBrowserFile(file, target, uploadId);
        }
      }
      throw error;
    }
    if (!cancelled || cancelled.uploadId !== uploadId || cancelled.status !== 'cancelled') throw new ApiError('Сервер не подтвердил отмену. Повторите отмену загрузки.', 502);
    return cancelled;
  } catch (error) {
    // The owner is checked by the server before 410; this also covers a lost DELETE response.
    if (error instanceof ApiError && error.status === 410) return { uploadId, status: 'cancelled' };
    throw error;
  }
}
